<template>
  <div class="task-subtask-form">
    <b-form>
      <!-- Categoria / Priorità / Stato -->
      <b-form-row>
        <form-thux-horizontal-select
          :validator="$v.form.category"
          :label-form="'Category' | translate"
          class-form="col-lg-4 mb-2 triple-row"
          labelColsSm="4"
          labelColsLg="4"
          label-search="text"
          :disabled="!formEditable"
          :value="macroSelectValue"
          :placeholder="$t('Select an option')"
          :options="macroOptions"
          @select="onSelectMacro"
          @clear="onClearMacro"
        />
        <task-priority-select
          :validator="$v.form.priority"
          :label-form="'Priority' | translate"
          class-form="col-lg-4 mb-2 triple-row"
          labelColsSm="4"
          labelColsLg="4"
          :disabled="!formEditable"
          :value="prioritySelectValue"
          :placeholder="$t('Select an option')"
          :options="taskPriorityOptions"
          @select="onSelectPriority"
          @clear="onClearPriority"
        />
        <task-state-select
          :label-form="'State' | translate"
          class-form="col-lg-4 mb-2 triple-row"
          labelColsSm="4"
          labelColsLg="4"
          :disabled="!formEditable"
          :value="stateSelectValue"
          :placeholder="$t('Select an option')"
          :options="stateOptions"
          @select="onSelectState"
          @clear="onClearState"
        />
      </b-form-row>

      <!-- Nome -->
      <b-form-row>
        <form-thux-horizontal-input
          :validator="$v.form.name"
          :label-form="'Name' | translate"
          class-form="col-lg-12 mb-2 description-row"
          labelColsSm="3"
          labelColsLg="3"
          type="text"
          :value="form.name"
          :disabled="!formEditable"
          @change="$set(form, 'name', $event)"
        />
      </b-form-row>

      <!-- Descrizione -->
      <b-form-row>
        <form-thux-horizontal-text-area
          :validator="$v.form.description"
          :label-form="'Description' | translate"
          class-form="col-lg-12 mb-2 description-row"
          labelColsSm="3"
          labelColsLg="3"
          :rows="3"
          :value="form.description"
          :disabled="!formEditable"
          @change="$set(form, 'description', $event)"
        />
      </b-form-row>

      <!-- Incaricati / Data scadenza -->
      <b-form-row>
        <form-thux-horizontal-multiselect
          :validator="$v.form.assignees"
          :label-form="'Assignees' | translate"
          class-form="col-lg-6 mb-2"
          labelColsSm="3"
          labelColsLg="3"
          label-search="name"
          :disabled="!formEditable"
          :value="assigneesMulti"
          :options="assigneeOptionsMulti"
          :placeholder="$t('Type to search')"
          :min-length="0"
          :internal-search="false"
          @search="onSearchAssignee"
          @select="onSelectAssignee"
          @remove="onRemoveAssignee"
          @clear="onClearAssignees"
        />
        <form-thux-horizontal-datetime
          :label-form="'Due date' | translate"
          class-form="col-lg-6 mb-2"
          labelColsSm="3"
          labelColsLg="3"
          type="date"
          :is-required="false"
          :disabled="!formEditable"
          :value="dueDate"
          :placeholder="$t('Select a date')"
          @change="onDueDateChange"
        />
      </b-form-row>

      <!-- Tag / Tempo impiegato -->
      <b-form-row>
        <form-thux-horizontal-multiselect
          :label-form="'Tags' | translate"
          class-form="col-lg-6 mb-2"
          labelColsSm="3"
          labelColsLg="3"
          label-search="name"
          :is-required="false"
          :disabled="!formEditable"
          :value="tagsMulti"
          :options="tagOptionsMulti"
          :placeholder="$t('Type to search')"
          :min-length="0"
          :taggable="formEditable"
          :tag-placeholder="$t('Press enter to create the tag')"
          @search="onSearchTag"
          @select="onSelectTag"
          @tag="onAddTag"
          @remove="onRemoveTag"
          @clear="onClearTags"
        />
        <form-thux-horizontal-duration
          :label-form="'Time spent' | translate"
          class-form="col-lg-6 mb-2"
          labelColsSm="3"
          labelColsLg="3"
          :disabled="!formEditable"
          :value="form.time_spent"
          @change="$set(form, 'time_spent', $event)"
        />
      </b-form-row>

      <!-- Preventivo collegato (preimpostato dal task padre, modificabile) -->
      <b-form-row v-if="!hideRelations">
        <form-thux-horizontal-autocomplete
          :label-form="'Related quotation' | translate"
          class-form="col-lg-6 mb-2"
          labelColsSm="3"
          labelColsLg="3"
          label-search="name"
          :is-required="false"
          :disabled="!formEditable"
          :value="quotationAutocompleteValue"
          :options="quotationOptionsMulti"
          :placeholder="$t('Type to search')"
          :min-length="0"
          :internal-search="false"
          @search="onSearchQuotation"
          @select="onSelectQuotation"
          @clear="onClearQuotation"
        />
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import slugify from 'slugify'

import TaskFormMixin from '../mixins/TaskFormMixin.js'
import TaskPrioritySelect from './TaskPrioritySelect.vue'
import TaskStateSelect from './TaskStateSelect.vue'

import FormThuxHorizontalAutocomplete from '@/components/form-thux-horizontal-components/FormThuxHorizontalAutocomplete'
import FormThuxHorizontalSelect from '@/components/form-thux-horizontal-components/FormThuxHorizontalSelect'
import FormThuxHorizontalInput from '@/components/form-thux-horizontal-components/FormThuxHorizontalInput'
import FormThuxHorizontalTextArea from '@/components/form-thux-horizontal-components/FormThuxHorizontalTextArea'
import FormThuxHorizontalMultiselect from '@/components/form-thux-horizontal-components/FormThuxHorizontalMultiselect'
import FormThuxHorizontalDatetimePicker from '@/components/form-thux-horizontal-components/FormThuxHorizontalDatetimePicker'
import FormThuxHorizontalDuration from '@/components/form-thux-horizontal-components/FormThuxHorizontalDuration'

export default {
  name: 'TaskSubtaskForm',
  mixins: [TaskFormMixin],
  components: {
    'task-priority-select': TaskPrioritySelect,
    'task-state-select': TaskStateSelect,
    'form-thux-horizontal-autocomplete': FormThuxHorizontalAutocomplete,
    'form-thux-horizontal-select': FormThuxHorizontalSelect,
    'form-thux-horizontal-input': FormThuxHorizontalInput,
    'form-thux-horizontal-text-area': FormThuxHorizontalTextArea,
    'form-thux-horizontal-multiselect': FormThuxHorizontalMultiselect,
    'form-thux-horizontal-datetime': FormThuxHorizontalDatetimePicker,
    'form-thux-horizontal-duration': FormThuxHorizontalDuration
  },
  props: {
    // ereditati dal task padre (organization, categoryTypeCode, assigneeFilters...)
    editParams: { type: Object, default: () => ({}) },
    formEditable: { type: Boolean, default: true },
    hideRelations: { type: Boolean, default: false },
    // snapshot dei campi del task padre per inizializzare la sottoattività
    initialValues: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      form: { assignees: [], tags: [] },
      dueDate: undefined
    }
  },
  created () {
    const init = this.initialValues || {}
    const form = { assignees: [], tags: [] }
    if (init.organization != null) {
      form.organization = init.organization
      if (init.organization_name) form.organization_name = init.organization_name
    }
    if (init.category != null) {
      form.category = init.category
      if (init.category_name) form.category_name = init.category_name
    }
    if (init.priority != null) {
      form.priority = init.priority
      if (init.priority_name) form.priority_name = init.priority_name
    }
    // stato dal padre, altrimenti default "Aperta" (il watch su stateOptions
    // lo completa se le opzioni non sono ancora caricate)
    form.state = init.state != null ? init.state : this.getStateIdByCode('open')
    if (Array.isArray(init.assignees) && init.assignees.length) {
      form.assignees = init.assignees.slice()
    }
    if (Array.isArray(init.assignees_detail) && init.assignees_detail.length) {
      form.assignees_detail = init.assignees_detail.map((a) => ({ ...a }))
    }
    if (init.due_date) form.due_date = init.due_date
    if (init.quotation != null) {
      form.quotation = init.quotation
      if (init.quotation_detail) form.quotation_detail = init.quotation_detail
    }
    this.$set(this, 'form', form)
    if (init.dueDate) this.$set(this, 'dueDate', init.dueDate)
  },
  computed: {
    macroSelectValue () {
      if (!this.form.category) return null
      const text = this.form.category_name || this.macroNameLookup(this.form.category)
      return { value: this.form.category, text: text || `#${this.form.category}` }
    },
    prioritySelectValue () {
      if (!this.form.priority) return null
      const text = this.priorityNameLookup(this.form.priority) || this.form.priority_name
      const color = this.priorityColorLookup(this.form.priority) || '#6c757d'
      return { value: this.form.priority, text: text || `#${this.form.priority}`, color }
    },
    stateSelectValue () {
      if (!this.form.state) return null
      const match = this.taskStateEntries && this.taskStateEntries.find((i) => i.id === this.form.state)
      const text = (match && match.name) || this.form.state_name || this.getStateDisplay(this.form.state)
      const color = (match && match.color) || '#6c757d'
      return { value: this.form.state, text: text || `#${this.form.state}`, color }
    },
    assigneesMulti () {
      if (!this.form.assignees) return []
      const items = this.ensureArray(this.form.assignees)
      if (items.length === 0) return []
      if (typeof items[0] === 'object') {
        return items.map((item) => ({
          id: item.id,
          name: (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email || `#${item.id}`).toUpperCase()
        }))
      }
      const detail = this.ensureArray(this.form.assignees_detail)
      return items.map((id) => {
        const d = detail.find((x) => x.id === id)
        if (d) {
          return {
            id,
            name: (d.full_name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.email || `#${id}`).toUpperCase()
          }
        }
        const u = this.assigneeOptionsMulti.find((opt) => opt.id === id)
        return { id, name: u ? u.name : `#${id}` }
      })
    },
    quotationAutocompleteValue () {
      const id = this.form.quotation
      if (id == null) return null
      const detail = this.form.quotation_detail
      if (detail && detail.id === id) {
        return { id, name: this.formatQuotationLabel(detail) }
      }
      const q = this.quotationOptionsMulti.find((opt) => opt.id === id)
      return { id, name: q ? q.name : `#${id}` }
    },
    tagsMulti () {
      if (!this.form.tags) return []
      const items = this.ensureArray(this.form.tags)
      if (items.length === 0) return []
      if (typeof items[0] === 'object') {
        return items.map((item) => ({ id: item.id, name: item.name }))
      }
      return items.map((id) => {
        const t = this.tagOptionsMulti.find((opt) => opt.id === id)
        return { id, name: t ? t.name : `#${id}` }
      })
    }
  },
  watch: {
    // default Stato = "Aperta" appena gli stati sono disponibili
    stateOptions: {
      immediate: true,
      handler () {
        if (!this.form.state) {
          const open = this.getStateIdByCode('open')
          if (open) this.$set(this.form, 'state', open)
        }
      }
    },
    // notifica il padre quando cambia la validità di questa sottoattività
    '$v.form.$invalid': {
      immediate: true,
      handler (invalid) {
        this.$emit('validity-change', !invalid)
      }
    },
    // il preventivo del task padre si propaga alle sottoattività
    'initialValues.quotation' (val) {
      this.$set(this.form, 'quotation', val != null ? val : null)
      this.$set(this.form, 'quotation_detail', (this.initialValues && this.initialValues.quotation_detail) || null)
    }
  },
  methods: {
    onSelectMacro (item) { this.selectSingleToValue(item, 'category', 'category_name') },
    onClearMacro () { this.clearSingle('category', 'category_name') },
    onSelectPriority (item) { this.selectSingleToValue(item, 'priority', 'priority_name') },
    onClearPriority () { this.clearSingle('priority', 'priority_name') },
    onSelectState (item) { this.$set(this.form, 'state', item.value) },
    onClearState () { this.$set(this.form, 'state', null) },

    onSearchAssignee (value) { this.fetchAssigneeOptions(value) },
    onSelectAssignee (item) {
      const current = this.ensureArray(this.form.assignees).slice()
      if (!current.includes(item.id)) current.push(item.id)
      this.$set(this.form, 'assignees', current)
      const detail = this.ensureArray(this.form.assignees_detail).slice()
      if (!detail.find((i) => i.id === item.id)) detail.push({ id: item.id, full_name: item.name })
      this.$set(this.form, 'assignees_detail', detail)
    },
    onRemoveAssignee (item) {
      this.$set(this.form, 'assignees', this.ensureArray(this.form.assignees).filter((id) => id !== item.id))
      this.$set(this.form, 'assignees_detail', this.ensureArray(this.form.assignees_detail).filter((i) => i.id !== item.id))
    },
    onClearAssignees () {
      this.$set(this.form, 'assignees', [])
      this.$set(this.form, 'assignees_detail', [])
    },

    onSearchTag (value) { this.fetchTagOptions(value) },
    onAddTag (name) {
      const label = (name || '').trim()
      if (!label) return
      this.createTaskTagAction({ name: label, slug: slugify(label, { remove: /[*+~.()'"!:@]/g }) }).then((created) => {
        if (!created || created.id == null) return
        this.onSelectTag({ id: created.id, name: created.name })
        this.fetchTagOptions()
      })
    },
    onSelectTag (item) {
      const current = this.ensureArray(this.form.tags).slice()
      if (!current.includes(item.id)) current.push(item.id)
      this.$set(this.form, 'tags', current)
      const detail = this.ensureArray(this.form.tags_detail).slice()
      if (!detail.find((i) => i.id === item.id)) detail.push({ id: item.id, name: item.name })
      this.$set(this.form, 'tags_detail', detail)
    },
    onRemoveTag (item) {
      this.$set(this.form, 'tags', this.ensureArray(this.form.tags).filter((id) => id !== item.id))
      this.$set(this.form, 'tags_detail', this.ensureArray(this.form.tags_detail).filter((i) => i.id !== item.id))
    },
    onClearTags () {
      this.$set(this.form, 'tags', [])
      this.$set(this.form, 'tags_detail', [])
    },

    onSearchQuotation (value) { this.fetchQuotationOptions(value) },
    onSelectQuotation (item) {
      this.$set(this.form, 'quotation', item.id)
      const raw = this.quotationList && this.quotationList.results
        ? this.quotationList.results.find((q) => q.id === item.id)
        : null
      this.$set(this.form, 'quotation_detail', raw
        ? { id: item.id, quotation_code: raw.quotation_code, number: raw.number, description: raw.description, order_done: raw.order_done, customer_name: raw.customer_name || (raw.customer && raw.customer.name) || null }
        : { id: item.id, description: item.name })
    },
    onClearQuotation () {
      this.$set(this.form, 'quotation', null)
      this.$set(this.form, 'quotation_detail', null)
    },

    onDueDateChange (evt) {
      const date = evt && evt.date
      if (date) {
        this.$set(this, 'dueDate', date)
        this.$set(this.form, 'due_date', date.format('YYYY-MM-DD'))
      } else {
        this.$set(this, 'dueDate', null)
        this.$set(this.form, 'due_date', null)
      }
    },

    macroNameLookup (id) {
      const match = this.macroOptions.find((o) => o.value === id)
      return match ? match.text : null
    },
    priorityNameLookup (id) {
      const match = this.taskPriorityOptions.find((o) => o.value === id)
      return match ? match.text : null
    },
    priorityColorLookup (id) {
      const match = this.taskPriorityOptions.find((o) => o.value === id)
      return match ? match.color : null
    },

    // --- API esposte al componente padre (TaskEdit) ---
    validate () {
      this.$v.form.$touch()
      return !this.$v.form.$invalid
    },
    getFormData () {
      const data = Object.assign({}, this.form)
      data.assignees = this.multiselectIdList(this.ensureArray(data.assignees))
      data.tags = this.multiselectIdList(this.ensureArray(data.tags))
      delete data.category_name
      delete data.priority_name
      delete data.state_name
      delete data.assignees_detail
      delete data.tags_detail
      delete data.quotation_detail
      return data
    }
  },
  validations: {
    form: {
      name: { required },
      category: { required },
      priority: { required },
      assignees: { required: (value) => Array.isArray(value) && value.length > 0 }
    }
  }
}
</script>
